import { useState } from 'react';
import { Alert, Button, Form, Input, Select, Space, Typography, message } from 'antd';
import Modal from 'antd/es/modal/Modal';
import { ApiOutlined, SearchOutlined } from '@ant-design/icons';
import { Authentication } from '@leedpulse-monorepo/classes';
import connectApimo, { ConnectApimoPayload } from '../../../../domain/use_cases/users/connectApimo';
import listApimoAgencyUsers, { ApimoAgencyUser } from '../../../../domain/use_cases/users/listApimoAgencyUsers';

interface ConnectApimoModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userEmails: string[];
  companies: { id: string; name: string }[];
  auth: Authentication | null;
  onSuccess: () => void;
}

const ConnectApimoModal: React.FC<ConnectApimoModalProps> = ({
  open,
  onClose,
  userId,
  userEmails,
  companies,
  auth,
  onSuccess,
}) => {
  const [companyId, setCompanyId] = useState<string | undefined>(companies.length === 1 ? companies[0].id : undefined);
  const [partnerUid, setPartnerUid] = useState('');
  const [matchedAgent, setMatchedAgent] = useState<ApimoAgencyUser | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const normalizedUserEmails = userEmails.filter(Boolean).map((e) => e.toLowerCase());

  const resetMatch = () => {
    setMatchedAgent(null);
    setHasSearched(false);
  };

  const handleLoadAgents = async () => {
    if (!partnerUid.trim()) {
      message.error("Renseigne d'abord l'ID de l'agence Apimo.");
      return;
    }
    setIsLoadingAgents(true);
    try {
      const list = await listApimoAgencyUsers(partnerUid.trim(), auth);
      // The agent is matched on email, exactly as the backend does — the id is never chosen by
      // hand, since a wrong one would silently give this user another agent's contacts.
      const matched = list.find((a) => normalizedUserEmails.includes(String(a.email).toLowerCase()));
      setMatchedAgent(matched ?? null);
      setHasSearched(true);
    } catch (e) {
      resetMatch();
      message.error(e instanceof Error ? e.message : 'Erreur lors du chargement des agents.');
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const handleSubmit = async () => {
    if (!auth) return;
    if (!companyId || !partnerUid.trim() || !matchedAgent) {
      message.error("Renseigne l'agence et retrouve l'agent correspondant.");
      return;
    }
    setIsBusy(true);
    try {
      await connectApimo(new ConnectApimoPayload(userId, companyId, partnerUid.trim()), auth);
      message.success('Agence Apimo connectée. Synchronisation lancée.');
      onSuccess();
      onClose();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Erreur lors de la connexion Apimo.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ApiOutlined size={20} style={{ marginRight: 6 }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Connecter Apimo</span>
        </div>
      }
      centered
      open={open}
      onCancel={onClose}
      okText="Connecter et synchroniser"
      cancelText="Annuler"
      confirmLoading={isBusy}
      okButtonProps={{ disabled: !matchedAgent }}
      onOk={handleSubmit}
      width={500}
    >
      <Form layout="vertical" style={{ marginTop: '2rem' }}>
        {companies.length > 1 && (
          <Form.Item label="Entreprise">
            <Select
              value={companyId}
              onChange={setCompanyId}
              placeholder="Sélectionner l'entreprise"
              options={companies.map((c) => ({ label: c.name, value: c.id }))}
            />
          </Form.Item>
        )}
        <Form.Item label="ID agence Apimo (agency_id)">
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={partnerUid}
              onChange={(e) => {
                setPartnerUid(e.target.value);
                resetMatch();
              }}
              onPressEnter={handleLoadAgents}
            />
            <Button icon={<SearchOutlined />} loading={isLoadingAgents} onClick={handleLoadAgents}>
              Retrouver l'agent
            </Button>
          </Space.Compact>
        </Form.Item>

        {hasSearched && matchedAgent && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
            message="Agent Apimo trouvé"
            description={`${matchedAgent.firstname} ${matchedAgent.lastname} — ${matchedAgent.email}`}
          />
        )}

        {hasSearched && !matchedAgent && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
            message="Aucun agent ne correspond"
            description={`Aucun agent de cette agence n'a l'email de l'utilisateur (${normalizedUserEmails.join(', ') || 'aucun email'}). Vérifie que c'est la bonne agence et la bonne personne, ou corrige l'email dans Apimo. Seuls les agents actifs et avec un email remontent.`}
          />
        )}

        <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
          Pour une entreprise déjà créée sans le connecteur Apimo. L'agent est retrouvé par son
          email : l'agence est renseignée sur l'entreprise, le connecteur ajouté sur l'utilisateur,
          puis une synchronisation complète des contacts est lancée. Sans ça, la découverte nocturne
          créerait une entreprise en double.
        </Typography.Text>
      </Form>
    </Modal>
  );
};

export default ConnectApimoModal;
